import { Button, OTPInput } from '@paguz/ui';
import { useState } from 'react';
import { customerMessage, isApiError } from '../../api/errors.ts';
import { InlineAlert } from '../../components/feedback.tsx';
import { config } from '../../config.ts';
import { formatPhone } from '../../lib/format.ts';

/** Paso de código OTP (ingreso y registro). En demo muestra el código para que nadie se quede atascado. */
export function OtpStep({
  phone,
  demoCode,
  onVerify,
  onChangePhone,
  onResend,
}: {
  phone: string;
  demoCode: string | undefined;
  onVerify: (code: string) => Promise<void>;
  onChangePhone: () => void;
  onResend: () => Promise<void>;
}) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(value: string) {
    if (value.length !== 6 || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onVerify(value);
    } catch (err) {
      setError(customerMessage(err));
      if (isApiError(err)) setCode('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(event) => {
        event.preventDefault();
        void submit(code);
      }}
    >
      <p className="text-muted-foreground">
        Escribe el código de 6 dígitos que enviamos al{' '}
        <strong className="text-foreground">{formatPhone(phone)}</strong>.
      </p>
      {config.demoMode && demoCode !== undefined && (
        <InlineAlert tone="info" title="Código de demostración">
          Usa <strong className="tracking-widest">{demoCode}</strong>. En producción llega por SMS.
        </InlineAlert>
      )}
      <OTPInput
        value={code}
        onChange={(value: string) => {
          setCode(value);
          if (value.length === 6) void submit(value);
        }}
        invalid={error !== null}
        autoFocus
        aria-describedby={error === null ? undefined : 'otp-error'}
      />
      {error !== null && (
        <p id="otp-error" role="alert" className="text-sm font-medium text-destructive">
          {error}
        </p>
      )}
      <Button type="submit" size="lg" loading={busy} disabled={code.length !== 6}>
        Continuar
      </Button>
      <div className="flex justify-between text-sm">
        <Button type="button" variant="link" onClick={onChangePhone}>
          Cambiar número
        </Button>
        <Button
          type="button"
          variant="link"
          onClick={() => {
            setError(null);
            setCode('');
            void onResend().catch((err: unknown) => {
              setError(customerMessage(err));
            });
          }}
        >
          Enviar otro código
        </Button>
      </div>
    </form>
  );
}
